import { useMemo } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { RefreshCw } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import StatusChip, { neutralDot, statusColor } from "./StatusChip";
import {
  statusLabel,
  statusShortLabel,
  useWorkflowConfig,
} from "../hooks/useWorkflow";
import { Tooltip } from "../ds";
import type { WorkflowConfig } from "../api/workflow";
import { formatDateTime, formatHistoryRemark } from "../utils/format";
import type {
  SuggestionHistoryItem,
  SuggestionStatus,
} from "../types/ess";

// Categories from the workflow yaml that count as "terminal" — once a
// suggestion lands in any of these, the timeline doesn't show further
// upcoming stages. Categories are a small, stable yaml vocabulary (not
// state names), so this set doesn't grow with new workflow states.
const TERMINAL_CATEGORIES = new Set<string>([
  "rejected",
  "closed",
  "completed",
  "done",
]);

// Every self-transition is activity. The backend writes one per
// in-stage action (reviewer assign/clear, per-department implementation),
// always with `from_status === to_status`, so the timeline doesn't need to
// recognise individual remark phrasings — a rule the previous
// remark-pattern matcher broke every time the backend reworded one.
function isActivityRow(h: SuggestionHistoryItem): boolean {
  return h.from_status === h.to_status;
}

// ─── Shared dot + spine + content primitive ──────────────────────────

interface TimelineRowProps {
  dotColor: string;
  isActive?: boolean;
  // hasNext drives the spine below this row; when false the row is the
  // last in its column and the spine terminates at this dot.
  hasNext: boolean;
  connectorColor?: string;
  ariaCurrent?: "step";
  ariaDisabled?: boolean;
  children: React.ReactNode;
}

// Vertical gap between milestones. The connector runs this far plus the
// next dot's 5px offset, so it always meets the next dot.
const ROW_GAP_PX = 16;

function TimelineRow({
  dotColor,
  isActive = false,
  hasNext,
  connectorColor,
  ariaCurrent,
  ariaDisabled,
  children,
}: TimelineRowProps) {
  return (
    <Box
      aria-current={ariaCurrent}
      aria-disabled={ariaDisabled}
      sx={{
        position: "relative",
        mb: hasNext ? `${ROW_GAP_PX}px` : 0,
        ...(hasNext && {
          "&::before": {
            content: '""',
            position: "absolute",
            // Centred under the 10px dot, running into the next row's.
            left: -16.5,
            top: 15,
            bottom: -(ROW_GAP_PX + 5),
            width: "1px",
            // grey.300 is a light-page hairline; on the dark page it reads
            // as a bright stripe, so dark takes the divider.
            bgcolor: (t: Theme) =>
              connectorColor ??
              (t.palette.mode === "dark" ? t.palette.divider : t.palette.grey[300]),
          },
        }),
      }}
    >
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          position: "absolute",
          // Centred on the row's first 20px line (the chip, or an
          // upcoming stage's label).
          left: isActive ? -22 : -21,
          top: isActive ? 4 : 5,
          width: isActive ? 12 : 10,
          height: isActive ? 12 : 10,
          borderRadius: "50%",
          bgcolor: dotColor,
          // Active halo uses the dot color in the outer ring, ringed by the
          // card surface. Upcoming rows never set isActive, so the
          // "grey.400" theme path that can't be interpolated into a colour
          // template literal never reaches this branch.
          boxShadow: isActive
            ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 3.5px ${dotColor}`
            : `0 0 0 2px ${theme.palette.background.paper}`,
        })}
      />
      {children}
    </Box>
  );
}

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
  p: 0,
  m: "-1px",
} as const;

/**
 * An upcoming stage's name as one muted line: the short label, with the
 * full one in a tooltip and in the accessible name (and a tab stop, only
 * when the short label hides text) — the same contract as StatusChip.
 */
function UpcomingLabel({
  workflow,
  status,
}: {
  workflow: WorkflowConfig | undefined;
  status: string;
}) {
  const full = statusLabel(workflow, status) || status;
  const short = statusShortLabel(workflow, status) || full;
  const hidesText = short !== full;
  return (
    <Tooltip title={full} placement="top" describeChild>
      <Typography
        component="span"
        tabIndex={hidesText ? 0 : undefined}
        sx={{
          fontSize: 12,
          lineHeight: "20px",
          fontWeight: 500,
          color: "text.secondary",
          borderRadius: 0.5,
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        {hidesText ? (
          <>
            <span aria-hidden="true">{short}</span>
            <Box component="span" sx={visuallyHidden}>
              {full}
            </Box>
          </>
        ) : (
          full
        )}
      </Typography>
    </Tooltip>
  );
}

interface Props {
  history: SuggestionHistoryItem[] | undefined;
  loading?: boolean;
  /**
   * Current status from the suggestion record. The active timeline row
   * is the one whose `to_status` matches this. Sourcing it from the
   * suggestion (not the last history index) closes the inconsistency
   * window where detail and history refetch out of sync after a
   * mutation. Falls back to last-history-row when no row matches yet.
   */
  currentStatus?: string;
  /**
   * Set when the parent's history query failed. Distinguishes "fetch
   * failed" from "no data yet" — without this the empty state silently
   * masks errors.
   */
  error?: boolean;
  onRetry?: () => void;
}

/**
 * Vertical status timeline, sized for the right rail. Renders one row per
 * milestone — a dense status chip over a single caption line (time · actor)
 * — plus one muted line for every canonical-flow stage the suggestion
 * hasn't reached yet (skipped when the suggestion is in a terminal state).
 * The active dot — the history row matching `currentStatus`, i.e. the same
 * status the header chip shows — is a size up and carries a halo ring.
 *
 * Self-transitions (`from_status === to_status`) are activity, not
 * milestones, and are left off the spine: a stage worked on repeatedly —
 * seats filled one by one, departments reporting their implementation —
 * still reads as one beat.
 */
export default function SuggestionTimeline({
  history,
  loading,
  currentStatus,
  error,
  onRetry,
}: Props) {
  const theme = useTheme();
  // Soft copper wash for remark callouts.
  const remarkBg = alpha(theme.palette.primary.main, 0.04);
  // Grey for submitted/draft dots — the NEUTRAL triple's dot is too close
  // to the chip background, so the spine uses a plain grey instead.
  const greyDot = theme.palette.grey[500];
  // Workflow config is fetched at app load with `staleTime: Infinity`
  // and is the single source of happy_path + terminal categories.
  // While it's loading, the component renders a spinner instead of
  // falling back to a stale local list.
  const { data: workflow, isLoading: workflowLoading } = useWorkflowConfig();
  const isTerminalStatus = useMemo(() => {
    return (status: string) => {
      const cat = workflow?.states?.[status]?.category;
      return cat ? TERMINAL_CATEGORIES.has(cat) : false;
    };
  }, [workflow]);
  const happyPath = useMemo<SuggestionStatus[]>(
    () => (workflow?.happy_path ?? []) as SuggestionStatus[],
    [workflow],
  );

  // Self-transitions (evaluations recorded, committee seats changed,
  // department marks) never move the suggestion along the pipeline, so they
  // are dropped from the spine: a stage worked on twenty times still
  // occupies one bubble, and the rail stays a status history.
  const mainRows = useMemo(
    () => (history ?? []).filter((r) => !isActivityRow(r)),
    [history],
  );

  const { upcoming, totalCount } = useMemo(() => {
    const reached = new Set(mainRows.map((h) => h.to_status));
    const terminated = mainRows.some((h) => isTerminalStatus(h.to_status));
    const upc = terminated
      ? ([] as SuggestionStatus[])
      : happyPath.filter((st) => !reached.has(st));
    return { upcoming: upc, totalCount: mainRows.length + upc.length };
  }, [mainRows, isTerminalStatus, happyPath]);

  // Active = the history row matching the suggestion's current status —
  // the same thing the header chip shows. State labels describe the
  // in-progress activity ("Under WC Review", "Under Verification"), so
  // highlighting the NEXT upcoming stage (the old behavior) read one
  // step ahead of the header (e.g. status `under_verification` showed
  // "Closed – Implemented · In-Progress"). Upcoming rows are never
  // active; they all render as faded "Upcoming".
  const activeHistoryIndex = useMemo(() => {
    if (mainRows.length === 0) return -1;
    if (currentStatus) {
      for (let i = mainRows.length - 1; i >= 0; i--) {
        if (mainRows[i].to_status === currentStatus) return i;
      }
    }
    return mainRows.length - 1;
  }, [mainRows, currentStatus]);

  if (loading || workflowLoading) {
    return <CircularProgress size={24} />;
  }
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<RavenIcon icon={RefreshCw} />}
              onClick={onRetry}
            >
              Retry
            </Button>
          ) : undefined
        }
      >
        <AlertTitle sx={{ mb: 0 }}>Couldn't load history</AlertTitle>
      </Alert>
    );
  }
  if (mainRows.length === 0) {
    return (
      <Typography color="text.secondary">No status updates yet.</Typography>
    );
  }

  return (
    <Box sx={{ ml: 1, pl: 2.5 }}>
      {mainRows.map((h, idx) => {
        const isActive = idx === activeHistoryIndex;
        // Past history rows are visually muted — only the active bubble
        // keeps its chip colour. Inactive history rows get the same
        // neutral grey dot + connector as upcoming rows so the spine
        // reads as "completed, dimmed" rather than a coloured trail.
        const mode = theme.palette.mode;
        const chipColors = statusColor(workflow, h.to_status, mode);
        const activeDotColor =
          chipColors.dot === neutralDot(mode) ? greyDot : chipColors.dot;
        const dotColor = isActive ? activeDotColor : greyDot;
        const hasNext = idx < totalCount - 1;
        const caption = `${formatDateTime(h.created_at)} · ${
          h.updated_by_name ?? h.updated_by ?? "system"
        }`;
        // All connectors below history rows take the neutral default
        // (TimelineRow) now that past rows themselves are grey — the
        // active halo is the only coloured beat on the spine.
        return (
          <TimelineRow
            key={h.id}
            dotColor={dotColor}
            isActive={isActive}
            hasNext={hasNext}
            ariaCurrent={isActive ? "step" : undefined}
          >
            {/* Flex, so the row is the chip's 20px rather than the
                parent's taller body line box around an inline chip. */}
            <Box sx={{ display: "flex" }}>
              <StatusChip status={h.to_status} short dense />
            </Box>
            <Typography
              title={caption}
              sx={{
                mt: "4px",
                fontSize: 11,
                lineHeight: 1.35,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {caption}
            </Typography>
            {h.remarks && (
              <Box
                sx={{
                  fontSize: 11.5,
                  lineHeight: 1.4,
                  mt: 1,
                  p: "4px 8px",
                  overflowWrap: "anywhere",
                  bgcolor: remarkBg,
                  borderRadius: 1,
                  borderLeft: "2px solid",
                  borderColor: "divider",
                }}
              >
                {formatHistoryRemark(h.remarks)}
              </Box>
            )}
          </TimelineRow>
        );
      })}
      {upcoming.map((st, uIdx) => {
        const absoluteIdx = mainRows.length + uIdx;
        const hasNext = absoluteIdx < totalCount - 1;
        return (
          <TimelineRow
            key={`upcoming-${st}`}
            dotColor="grey.400"
            hasNext={hasNext}
            ariaDisabled
          >
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={0.75}
              sx={{ opacity: 0.75 }}
            >
              <UpcomingLabel workflow={workflow} status={st} />
              <Typography
                sx={{
                  fontSize: 11,
                  lineHeight: "20px",
                  color: "text.secondary",
                  fontStyle: "italic",
                  whiteSpace: "nowrap",
                }}
              >
                Upcoming
              </Typography>
            </Stack>
          </TimelineRow>
        );
      })}
    </Box>
  );
}
